import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Download, Share2, RefreshCw, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudioResultPage = () => {
  const { slug, jobId } = useParams();
  const [studio, setStudio] = useState(null);
  const [render, setRender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [submittingLead, setSubmittingLead] = useState(false);
  
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studioRes, renderRes] = await Promise.all([
          axios.get(`${API}/studios/public/${slug}`),
          axios.get(`${API}/renders/${jobId}`)
        ]);
        setStudio(studioRes.data);
        setRender(renderRes.data);

        // Poll if still processing
        if (renderRes.data.status === 'pending' || renderRes.data.status === 'processing') {
          const interval = setInterval(async () => {
            const res = await axios.get(`${API}/renders/${jobId}`);
            setRender(res.data);
            if (res.data.status !== 'pending' && res.data.status !== 'processing') {
              clearInterval(interval);
            }
          }, 3000);
          return () => clearInterval(interval);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, jobId]);

  const handleDownload = () => {
    if (render?.result_image_url) {
      const link = document.createElement('a');
      link.href = render.result_image_url;
      link.download = `wrap-preview-${jobId}.png`;
      link.click();
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: 'My Wrap Preview', url });
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setSubmittingLead(true);

    try {
      await axios.post(`${API}/leads`, {
        studio_id: studio.id,
        render_id: jobId,
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone || null,
        message: 'Quote request from visualizer'
      });
      
      toast.success('Quote request sent! We\'ll be in touch.');
      setShowLeadForm(false);
    } catch (error) {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setSubmittingLead(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (!studio || !render) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Result Not Found</h1>
          <Link to={`/s/${slug}`} className="text-[#F5D061] hover:underline">Go back to studio</Link>
        </div>
      </div>
    );
  }

  const accentColor = studio.accent_color || '#FFFFFF';
  const isProcessing = render.status === 'pending' || render.status === 'processing';
  const isCompleted = render.status === 'completed';
  const isFallback = render.status === 'fallback';

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="result-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link 
              to={`/s/${slug}`}
              className="flex items-center gap-2 text-white/60 hover:text-white"
            >
              <ArrowLeft size={18} />
              <span className="font-bold" style={{ color: accentColor }}>{studio.name}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Processing State */}
            {isProcessing && (
              <div className="text-center py-24">
                <div className="spinner w-16 h-16 mx-auto mb-8" style={{ borderTopColor: accentColor }}></div>
                <h2 className="text-2xl font-bold mb-4">Generating Your Preview</h2>
                <p className="text-white/60">This may take up to 60 seconds...</p>
                <p className="text-white/40 text-sm mt-4">Please don't close this page</p>
              </div>
            )}

            {/* Completed State */}
            {isCompleted && render.result_image_url && (
              <>
                <h1 className="text-3xl font-bold mb-2 text-center">Your Wrap Preview</h1>
                <p className="text-white/60 text-center mb-8">Here's how your car would look!</p>

                {/* Result Image */}
                <div className="bg-[#0F0F0F] border border-white/10 rounded-sm overflow-hidden mb-8">
                  <img 
                    src={render.result_image_url} 
                    alt="Wrap Preview"
                    className="w-full"
                    data-testid="result-image"
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-4 justify-center mb-12">
                  <Button
                    onClick={handleDownload}
                    className="font-semibold"
                    style={{ backgroundColor: accentColor, color: '#000' }}
                    data-testid="download-btn"
                  >
                    <Download className="mr-2 w-4 h-4" />
                    Download
                  </Button>
                  <Button
                    onClick={handleShare}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5"
                    data-testid="share-btn"
                  >
                    <Share2 className="mr-2 w-4 h-4" />
                    Share
                  </Button>
                  <Link to={`/s/${slug}/visualize`}>
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5"
                    >
                      <RefreshCw className="mr-2 w-4 h-4" />
                      Try Another
                    </Button>
                  </Link>
                </div>

                {/* Lead Capture */}
                {!showLeadForm ? (
                  <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-8 text-center">
                    <MessageSquare className="w-10 h-10 mx-auto mb-4" style={{ color: accentColor }} />
                    <h3 className="text-xl font-bold mb-2">Like what you see?</h3>
                    <p className="text-white/60 mb-6">Request a quote and we'll get back to you shortly</p>
                    <Button
                      onClick={() => setShowLeadForm(true)}
                      className="font-semibold"
                      style={{ backgroundColor: accentColor, color: '#000' }}
                      data-testid="request-quote-btn"
                    >
                      Request a Quote
                    </Button>
                  </div>
                ) : (
                  <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-8">
                    <h3 className="text-xl font-bold mb-6">Request a Quote</h3>
                    <form onSubmit={handleLeadSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="name" className="text-white/70 mb-2 block">Name *</Label>
                        <Input
                          id="name"
                          type="text"
                          value={leadForm.name}
                          onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                          className="bg-[#171717] border-white/10 h-12"
                          data-testid="lead-name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-white/70 mb-2 block">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={leadForm.email}
                          onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                          className="bg-[#171717] border-white/10 h-12"
                          data-testid="lead-email"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-white/70 mb-2 block">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={leadForm.phone}
                          onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                          className="bg-[#171717] border-white/10 h-12"
                          data-testid="lead-phone"
                        />
                      </div>
                      <div className="flex gap-4">
                        <Button
                          type="submit"
                          disabled={submittingLead}
                          className="flex-1 font-semibold"
                          style={{ backgroundColor: accentColor, color: '#000' }}
                          data-testid="submit-lead-btn"
                        >
                          {submittingLead ? <span className="spinner w-5 h-5"></span> : 'Send Request'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                          onClick={() => setShowLeadForm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
              </>
            )}

            {/* Fallback State */}
            {isFallback && (
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-4">Preview Partially Generated</h2>
                <p className="text-white/60 mb-8">
                  We couldn't generate a full preview at this time, but you can still request a quote!
                </p>
                <div className="flex gap-4 justify-center">
                  <Link to={`/s/${slug}/visualize`}>
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5"
                    >
                      Try Again
                    </Button>
                  </Link>
                  <Link to={`/s/${slug}/contact`}>
                    <Button
                      className="font-semibold"
                      style={{ backgroundColor: accentColor, color: '#000' }}
                    >
                      Contact Studio
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default StudioResultPage;
